import React, { useState, useMemo, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Search, Filter, ArrowUp, ArrowDown } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../Components/UI/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/UI/table";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
} from "../../Components/UI/drawer";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import PeriodFilter from "../../Components/Common/PeriodFilter";
import { cn } from "../../Lib/Cn";
import { RiResetLeftFill } from "react-icons/ri";
import useIsMobile from "../../Hooks/useIsMobile";
import { getLossAnallyticsAPI } from "../Threads/ThreadsAPI";
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import {
  getPastMonthOptions,
  months,
  getDateRangeOptions,
  getPastQuarterOptions,
  getOtherYearOptions,
  FormatAmount,
  currentUserHasPermission,
} from "../../Utils/Utilities";
import { useSelector } from "react-redux";
import {
  getUsersArr,
  checkIfInqOnlyMode,
  getThreadTypesArr,
  selectDisqlReasons,
  selectRejectionReasons,
  isSmartSellerMode,
} from "../../Store/Reducers";
import { TooltipProvider } from "../../Components/UI/tooltip";
import { useLocation, useNavigate } from "react-router-dom";
import Pagination from "../../Components/Common/Pagination";
import { formatAmount } from "../../Utils/Utilities";
import { TrendingDown, UserX } from "lucide-react";
import { useCurrency } from "../../Hooks/useCurrency";
import { format } from "date-fns";

// Add styles for rounded legend
const legendStyle = `
  .recharts-legend-wrapper svg rect {
    rx: 4 !important;
    ry: 4 !important;
  }
  *:focus {
    outline: none !important;
  }
`;

const themeColors = [
  "#0c6980",
  "#0b5d1e",
  "#d4af37",
  "#bf5700",
  "#230e2eff",
  "#c44536",
  "#2a9d8f",
  "#46210fff",
  "#3d348b",
  "#264653",
];

const getCurrentMonthYear = () => {
    const now = new Date();
    return `${months[now.getMonth()]} ${now.getFullYear()}`; // e.g., "January 2026"
};

const getCurrentQuarterYear = () => {
    const now = new Date();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${q} ${now.getFullYear()}`; // e.g., "Q1 2026"
};

const ThrLossAnalysis = () => {
  const isMobile = useIsMobile();
  const users = useSelector(getUsersArr);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  const threadTypes = useSelector(getThreadTypesArr);
  const disqlReasons = useSelector(selectDisqlReasons);
  const rejectionReasons = useSelector(selectRejectionReasons);
  const location = useLocation();
  const navigate = useNavigate();
  const { data: allConns = [] } = useSelector((state) => state.connectionData);
  const activeData = useSelector((state) => state.activeData);
  const thrViewTypeID = currentUserHasPermission('ThrViewTypeID');
  const isAssignedOnly = thrViewTypeID === 2;

  const monthlyJson = useMemo(() => {
    const raw = activeData.data.MonthlyThreads;
    // If it's a string, parse it; if it's already an array, use it; otherwise empty array
    if (typeof raw === 'string') return JSON.parse(raw);
    if (Array.isArray(raw)) return raw;
    return [];
  }, [activeData.data.MonthlyThreads]);

const getFilterFromParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const now = new Date();

   const currentMonthYear = getCurrentMonthYear();
    const currentQuarterYear = getCurrentQuarterYear();
    const currentYearStr = now.getFullYear().toString();

    // 1. Thread Type (tid)
    const defaultTID = threadTypes.find(t => t.Flow === "In" && t.TypSng === "Sales Inquiry")?.TID || 1;
    const threadType = parseInt(searchParams.get("tid") || searchParams.get("threadType") || defaultTID);

    // 2. Executive
    const executive = searchParams.get("eid") || searchParams.get("uid") || 'all';

    // 3. Date/Period
    const dateRange = searchParams.get("p") || "This Month";

   return {
      filters: {
        period: searchParams.get("p") || "This Month",
        dateRange: searchParams.get("p") || "This Month",
        otherMonth: searchParams.get("om") || currentMonthYear,
        otherQuarter: searchParams.get("oq") || currentQuarterYear,
        otherYear: searchParams.get("oy") || currentYearStr,
        assignedTo: searchParams.get("eid") || searchParams.get("uid") || 'all',
        threadType: parseInt(searchParams.get("tid") || searchParams.get("threadType") || 1),
      },
      standalone: {
        valueType: searchParams.get("vType") === '2' ? 'value' : 'count',
        reasonType: searchParams.get("rType") === '2' ? 'DISQRSN' : 'FLRSN'
      }
    };
  };

  const initialParams = getFilterFromParams();

  // State Management
  const [isLoading, setIsLoading] = useState(false);
  const [valueType, setValueType] = useState(initialParams.standalone.valueType);
  const [reasonType, setReasonType] = useState(initialParams.standalone.reasonType);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterSelected, setIsFilterSelected] = useState(isMobile ? false : true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [rawData, setRawData] = useState([]);

  // Fix #3: Default Desktop Sort set to 'totalValue' descending
  const [sortConfig, setSortConfig] = useState({
    key: "totalValue",
    direction: "desc",
  });

  // Pagination State
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const isInitialMount = React.useRef(true);

  // Filter state - initialize from URL params
  const [selectedFilters, setSelectedFilters] = useState({
    ...initialParams.filters,
    category: 'all'
  });
  const [tempFilters, setTempFilters] = useState({
    ...initialParams.filters,
    category: 'all'
  });

  // Extract distinct categories from raw data (exclude empty strings)
  const distinctCategories = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];
    const categories = [...new Set(rawData.map(item => item.category).filter(cat => cat && cat.trim() !== ''))];
    return categories.sort();
  }, [rawData]);

  const oldestSinceDT = useMemo(() => {
    if (!allConns || allConns.length === 0) return new Date();

    const dates = allConns.map(conn => {
      const dateStr = conn.SinceDT || conn.CreatedDT;
      return dateStr ? new Date(dateStr) : null;
    }).filter(d => d !== null && !isNaN(d));

    if (dates.length === 0) return new Date();

    // Return the minimum (oldest) date
    return new Date(Math.min(...dates));
  }, [allConns]);
  const availablePeriods = useMemo(() => {
    const monthsSet = new Set();
    const quartersSet = new Set();
    const yearsSet = new Set();

    const now = new Date();
    const currentMonthName = months[now.getMonth()];
    const currentYear = now.getFullYear();

    // 1. Add current periods as fallback
    monthsSet.add(`${currentMonthName} ${currentYear}`);
    quartersSet.add(`Q${Math.ceil((now.getMonth() + 1) / 3)} ${currentYear}`);
    yearsSet.add(currentYear.toString());

    // 2. Extract from JSON provided by the store
    if (Array.isArray(monthlyJson)) {
        monthlyJson.forEach(item => {
            if (item.YY && item.MM) {
                const y = item.YY.toString();
                const mName = months[item.MM - 1];
                const q = Math.ceil(item.MM / 3);
                if (mName) monthsSet.add(`${mName} ${y}`);
                quartersSet.add(`Q${q} ${y}`);
                yearsSet.add(y);
            }
        });
    }

    // 3. Sort Newest to Oldest
    const sortedMonths = Array.from(monthsSet).sort((a, b) => new Date(b) - new Date(a));
    const sortedQuarters = Array.from(quartersSet).sort((a, b) => {
        const [qA, yA] = a.split(" ");
        const [qB, yB] = b.split(" ");
        return yB !== yA ? parseInt(yB) - parseInt(yA) : qB.localeCompare(qA);
    });
    const sortedYears = Array.from(yearsSet).sort((a, b) => parseInt(b) - parseInt(a));

    return {
        months: sortedMonths,
        quarters: sortedQuarters,
        years: sortedYears,
    };
}, [monthlyJson]);
  // Dynamic Thread Type Filter (Hidden if Inquiry Only Mode)
  const allowedThreadTypes = ["Sales Inquiry", "Sales Order", "Support Ticket"];
  // const threadTypeFilter = !isInqOnlyMode ? [{
  //   key: "threadType",
  //   label: "All Types",
  //   options: threadTypes.filter(t => t.Flow === "In" && allowedThreadTypes.includes(t.TypSng)).map(t => ({
  //     name: t.TypSng,
  //     ID: t.TID
  //   })),
  //   displayValue: (val) => {
  //     const thread = threadTypes.find(t => t.TID === val && t.Flow === "In" && allowedThreadTypes.includes(t.TypSng));
  //     return thread?.TypSng || 'Sales Inquiry';
  //   },
  //   type: "list",
  //   mobileDropdownText: "Select Thread Type",
  //   MobileLabel: "All Types",
  //   showAllOption: false,
  //   showAllOptionNum: false,
  // }] : [];
  // Removed Thread Type Filter as requested
  const threadTypeFilter = [];

  // Fix #1 & #2: Added Executive Filter, Removed Reason Filter
  const filterOptions = [

    {
      key: "dateRange",
      label: "This Month",
      options: getDateRangeOptions(),
      searchEnabled: false,
      type: "options",
      MobileLabel: "Period",
      showAllOption: false,
      searchEnabled: true,
      mobileLabel: "Period",
    },
    // ...threadTypeFilter,

    {
      key: "otherMonth",
      label: "Select Month",
      options: availablePeriods.months, // UPDATED
      disabled: tempFilters.dateRange !== "Other Month",
      mobiledisabled: tempFilters.dateRange !== "Other Month",
      type: "list",
      displayValue: (val) => val || getCurrentMonthYear(),
    },
    {
      key: "otherQuarter",
      label: "Select Quarter",
      options: availablePeriods.quarters, // UPDATED
      disabled: tempFilters.dateRange !== "Other Quarter",
      mobiledisabled: tempFilters.dateRange !== "Other Quarter",
      type: "list",
    },
    {
      key: "otherYear",
      label: "Select Year",
      options: availablePeriods.years, // UPDATED
      disabled: tempFilters.dateRange !== "Other Year",
      mobiledisabled: tempFilters.dateRange !== "Other Year",
      type: "list",
      displayValue: (val) => val === "all" ? "Select Year" : val,
    },
    ...(!isSmartSellerMode() ? [{
      key: "assignedTo",
      label:
        selectedFilters.assignedTo === "unassigned"
          ? "Not Assigned"
          : selectedFilters.assignedTo === "all"
            ? "All Executives"
            : users.find(
              (u) => String(u.userID) === String(selectedFilters.assignedTo)
            )?.name || "All Executives",
      options: [
        { name: "Not Assigned", ID: "unassigned" },
        ...users
          .map((u) => ({ name: u.name, ID: u.userID }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ],
      displayValue: (val) => {
        if (val === "unassigned") return "Not Assigned";
        if (val === "all") return "All Executives";
        const foundUser = users.find((u) => String(u.userID) === String(val));
        return foundUser?.name || "All Executives";
      },
      type: "list",
      mobileDropdownText: "Select Executive",
      MobileLabel: "Executive",
      showAllOption: true,
      searchEnabled: true,
      mobileLabel: "Executive",
    }] : []),
    ...(distinctCategories.length > 0 ? [{
      key: "category",
      label: selectedFilters.category === "all" ? "All Categories" : selectedFilters.category,
      options: distinctCategories.map((cat) => ({ name: cat, ID: cat })),
      displayValue: (val) => val === "all" ? "All Categories" : val,
      type: "list",
      mobileDropdownText: "Select Category",
      MobileLabel: "Category",
      showAllOption: true,
      searchEnabled: true,
      mobileLabel: "Category",
    }] : []),

  ];

  // Fetch data from API
  const fetchProductAnalytics = async () => {
    try {
      setIsLoading(true);
      const today = new Date();

      let prdType = 'MM';
      let prdQty = 1;
      let year = today.getFullYear();
      let month = today.getMonth() + 1;

      const currentRange = tempFilters.dateRange;

      if (currentRange === 'Last Month') {
        const lastM = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        month = lastM.getMonth() + 1;
        year = lastM.getFullYear();
      } if (currentRange === 'Other Month') {
        const [mName, yStr] = tempFilters.otherMonth.split(' ');
        month = months.indexOf(mName) + 1;
        year = parseInt(yStr);
        prdType = 'MM';
      } else if (currentRange === 'This Quarter') {
        prdType = `QQ${Math.ceil(month / 3)}`;
      } else if (currentRange === 'Other Quarter') {
        const [qStr, yStr] = tempFilters.otherQuarter.split(' ');
        prdType = `QQ${qStr.replace('Q', '')}`;
        year = parseInt(yStr);
        month = 0; 
      } else if (currentRange === 'This Year') {
        prdType = 'YY';
      } else if (currentRange === 'Other Year') {
        prdType = 'YY';
        year = parseInt(tempFilters.otherYear);
        month = 0;
      }

      const payload = {
        threadTypeID: tempFilters.threadType || 1,
        prdType: prdType,
        prdQty: prdQty,
        year: year || today.getFullYear(),
        month: month,
        changeType: reasonType
      };

      const result = await getLossAnallyticsAPI(payload);
      checkAPIResponse(result);

      if (result && result.updatePackage) {
        try {
          const updtPkg = JSON.parse(result.updatePackage);

          const response = updtPkg.Table?.[0];
          if (response && parseInt(response.FnErrCode) !== -1) {
            console.error('API Error:', response.ErrMsg);
            setRawData([]);
            return;
          }

          const productAnalysis = updtPkg.Table1 || [];

          const transformedData = productAnalysis.map(item => ({
            // SQL returns 'Reason', we map it to internal property 'failureReason'
            failureReason: item.Reason || 'No Reason Specified',
            POCUserID: item.POCUserID,
            CntThread: item.CntThread,
            ThreadIDs: item.ThreadIDs,
            BizProspect: item.BizProspect,
            category: item.Segment || '',
          }));

          setRawData(transformedData);
        } catch (error) {
          console.error('Error parsing API response:', error);
          setRawData([]);
        }
      } else {
        setRawData([]);
      }
    } catch (error) {
      console.error('Error fetching product analytics:', error);
      setRawData([]);
    } finally {
      setIsLoading(false);
    }
  };
  // Set dynamic tab title based on thread type and reason type
  useEffect(() => {
    const thread = threadTypes.find(t => t.TID === parseInt(selectedFilters.threadType));
    const typeName = thread?.TypSng || "Thread";

    // 2. Determine if it's Failure or Disqualification
    const modeName = reasonType === "DISQRSN" ? "Disqualification" : "Failure";

    // 3. Set the document title
    document.title = `${typeName} ${modeName} Analysis`;
    return () => {
      document.title = "SahiClub";
    };

  }, [selectedFilters.threadType, reasonType, threadTypes]);
  // Fetch data on filter changes
  useEffect(() => {
    fetchProductAnalytics();
  }, [tempFilters, reasonType]);

  // Initialize filters from URL params - Apply filters but don't show filter UI
  // useEffect(() => {
  //   const params = getFilterFromParams();

  //   if (JSON.stringify(params.filters) !== JSON.stringify(selectedFilters)) {
  //     // Preserve category field when updating from URL params
  //     const filtersWithCategory = {
  //       ...params.filters,
  //       category: selectedFilters.category || 'all'
  //     };
  //     setSelectedFilters(filtersWithCategory);
  //     setTempFilters(filtersWithCategory);
  //   }

  //   if (params.standalone.valueType !== valueType) {
  //     setValueType(params.standalone.valueType);
  //   }

  //   if (params.standalone.reasonType !== reasonType) {
  //     setReasonType(params.standalone.reasonType);
  //   }


  // }, [location.search]);
useEffect(() => {
    const params = getFilterFromParams();

    // This ensures that even if you navigate to the page without params, 
    // the Drawer knows to select "January 2026" by default.
    setSelectedFilters(prev => ({
        ...prev,
        ...params.filters,
        category: prev.category // preserve category
    }));
    
    setTempFilters(prev => ({
        ...prev,
        ...params.filters,
        category: prev.category // preserve category
    }));

    if (params.standalone.valueType !== valueType) setValueType(params.standalone.valueType);
    if (params.standalone.reasonType !== reasonType) setReasonType(params.standalone.reasonType);

}, [location.search]);
  const hasActiveFilters = useMemo(() => {
    const currentMonth = getCurrentMonthYear();
    const currentQuarter = getCurrentQuarterYear();
    const currentYear = new Date().getFullYear().toString();

    // 1. Date filters: Active if not "This Month" or if period changed
    let isDateFiltered = false;

    if (selectedFilters.dateRange !== "This Month") {
      isDateFiltered = true;
    }

    // 2. Dropdown filters: Active if not "all"
    const isExecFiltered = selectedFilters.assignedTo !== 'all';
    const isCategoryFiltered = selectedFilters.category !== "all";

    // 3. Search bar
    const isSearchFiltered = searchTerm.trim() !== "";

    return isDateFiltered || isExecFiltered || isSearchFiltered || isCategoryFiltered;
  }, [selectedFilters, searchTerm]);
  // Listen for mobile header filter click
  useEffect(() => {
    const handleHeaderFilterClick = () => {
      setIsFilterSelected(true);
      setIsDrawerOpen(true);
    };
    window.addEventListener('open-loss-analysis-filter', handleHeaderFilterClick);
    return () => window.removeEventListener('open-loss-analysis-filter', handleHeaderFilterClick);
  }, []);

  // SYNC FILTERS TO URL (Fixes the issue where Desktop filters don't update URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Helper to set/delete params
    const updateParam = (key, val, defaultVal) => {
      if (val && val !== defaultVal) params.set(key, val);
      else params.delete(key);
    };

    updateParam('tid', selectedFilters.threadType, 1); // Assuming 1 is default, or logic handles it
    updateParam('eid', selectedFilters.executive, 'all');

    const dateRangeParam = (selectedFilters.dateRange === 'all') ? 'This Month' : selectedFilters.dateRange;
    updateParam('p', dateRangeParam, 'This Month');

    // Time Details
    if (selectedFilters.dateRange === 'Other Month') updateParam('om', selectedFilters.otherMonth, '');
    else params.delete('om');

    if (selectedFilters.dateRange === 'Other Quarter') updateParam('oq', selectedFilters.otherQuarter, 'all');
    else params.delete('oq');

    if (selectedFilters.dateRange === 'Other Year') updateParam('oy', selectedFilters.otherYear, 'all');
    else params.delete('oy');

    updateParam('reason', selectedFilters.specificReason, 'all');

    // Standalone types
    params.set('vType', valueType === 'value' ? '2' : '1');
    params.set('rType', reasonType === 'DISQRSN' ? '2' : '1');

    const newUrl = `${window.location.pathname}?${params.toString()}`;

    // Only push state if URL actually changed to prevent loops
    if (newUrl !== window.location.pathname + window.location.search) {
      // Use replaceState on initial mount, pushState on subsequent changes
      if (isInitialMount.current) {
        window.history.replaceState({}, '', newUrl);
        isInitialMount.current = false;
      } else {
        window.history.pushState({}, '', newUrl);
      }
    }
  }, [valueType, reasonType, selectedFilters]);

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // DATA PROCESSING: Unified for Table AND Graphs (Respects Executive Filter)
  const processedTableData = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return { data: [], totalCount: 0, totalValue: 0, filteredCount: 0 };
    }

    // Filter by executive if selected
    let filtered = rawData;

    // 1. Executive Filter (Fix #1: This now affects graphs too)
    if (tempFilters.assignedTo !== "all") {
      if (tempFilters.assignedTo === "unassigned") {
        // Filter for records where POCUserID is null, undefined, or 0
        filtered = filtered.filter(
          (item) => !item.POCUserID || item.POCUserID === 0
        );
      } else {
        filtered = filtered.filter(
          (item) => String(item.POCUserID) === String(tempFilters.assignedTo)
        );
      }
    }

    // 2. Category Filter
    if (tempFilters.category !== "all") {
      filtered = filtered.filter(
        (item) => item.category === tempFilters.category
      );
    }


    // Group by failure reason
    const reasonMap = {};
    filtered.forEach((item, idx) => {
      const reason = item.failureReason || 'No Reason Specified';
      if (!reasonMap[reason]) {
        reasonMap[reason] = {
          failureReason: reason,
          threadCount: 0,
          totalValue: 0,
          executives: []
        };
      }
      // Sum up Total Counts
      reasonMap[reason].threadCount += item.CntThread || 0;
      reasonMap[reason].totalValue += item.BizProspect || 0;
      if (item.POCUserID && !reasonMap[reason].executives.includes(item.POCUserID)) {
        reasonMap[reason].executives.push(item.POCUserID);
      }
    });
    const aggregated = Object.values(reasonMap);
    const filteredTotalCount = aggregated.reduce(
      (sum, item) => sum + item.threadCount,
      0
    );
    const filteredTotalValue = aggregated.reduce(
      (sum, item) => sum + item.totalValue,
      0
    );

    // 3. Calculate Percentages
    const withPercentages = aggregated.map((item) => ({
      ...item,
      countShare:
        filteredTotalCount > 0
          ? ((item.threadCount / filteredTotalCount) * 100).toFixed(2)
          : 0,
      valueShare:
        filteredTotalValue > 0
          ? ((item.totalValue / filteredTotalValue) * 100).toFixed(2)
          : 0,
    }));

    // 4. Search Filter (by Reason Name)
    const searchFiltered = searchTerm
      ? withPercentages.filter((item) =>
        item.failureReason.toLowerCase().includes(searchTerm.toLowerCase())
      )
      : withPercentages;

    return {
      data: searchFiltered,
      totalCount: filteredTotalCount,
      totalValue: filteredTotalValue,
      filteredCount: filtered.length,
    };
  }, [rawData, tempFilters, searchTerm]);


  // Sorting & Pagination
  const currentTableData = useMemo(() => {
    if (!processedTableData.data || processedTableData.data.length === 0)
      return [];

    let dataToSort = [...processedTableData.data];

    // Fix #4: Mobile Sorting based on Tab (ValueType)
    if (isMobile) {
      if (valueType === "count") {
        dataToSort.sort((a, b) => b.threadCount - a.threadCount);
      } else {
        dataToSort.sort((a, b) => b.totalValue - a.totalValue);
      }
      return dataToSort; // Return immediately for mobile
    }

    // Desktop Sorting (Uses sortConfig)
    if (sortConfig.key) {
      dataToSort = dataToSort.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "failureReason") {
          aValue = aValue ? aValue.toLowerCase() : "";
          bValue = bValue ? bValue.toLowerCase() : "";
          return sortConfig.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      });
    }

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return dataToSort.slice(startIndex, endIndex);
  }, [
    processedTableData.data,
    sortConfig,
    currentPage,
    rowsPerPage,
    isMobile,
    valueType,
  ]);

  // Handle filter apply
  const handleApplyFilters = () => {
    // We just update the state. The useEffect defined above will detect the change and update the URL automatically.
    setSelectedFilters(tempFilters);
    setIsDrawerOpen(false);
    setIsFilterSelected(false);
  };

  // Handle cancel filters
  const handleCancelFilters = () => {
    setTempFilters(selectedFilters);
    setIsDrawerOpen(false);
    setIsFilterSelected(false);
  };

  const handleResetFilters = () => {
    const now = new Date();
    const defaultTID = threadTypes.find((t) => t.Flow === "In" && t.TypSng === "Sales Inquiry")?.TID || 1;

    const resetFilters = {
      period: "This Month",
      dateRange: "This Month",
      otherMonth: getCurrentMonthYear(),
      otherQuarter: getCurrentQuarterYear(),
      otherYear: now.getFullYear().toString(),
      assignedTo: "all",
      threadType: defaultTID,
      category: "all",
    };

    setTempFilters(resetFilters);
    setSelectedFilters(resetFilters);
    setSearchTerm("");

    // Update URL to clean state but preserve mode
    const params = new URLSearchParams();
    params.set("rType", reasonType === "DISQRSN" ? "2" : "1");
    params.set("vType", valueType === "value" ? "2" : "1");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };
  const handleSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  const handleRowClick = (reason) => {
    const reasonsArray = reasonType === 'DISQRSN' ? disqlReasons : rejectionReasons;
    const threadReasons = reasonsArray.find((r) => r.TID === parseInt(selectedFilters.threadType));

    if (!threadReasons) return;

    const parsedReasons = JSON.parse(threadReasons.Rsns);
    const reasonObj = parsedReasons.find((r) => r.R === reason);

    if (!reasonObj) return;

    const rID = reasonObj.RID;
    const params = new URLSearchParams();
    params.append('tID', selectedFilters.threadType);
    params.append('rTyp', reasonType === 'DISQRSN' ? 'DSQL' : 'REJN');
    params.append('rID', rID);
    params.append('sID', reasonType === 'DISQRSN' ? '4' : '3');

    params.append('p', selectedFilters.dateRange);

    if (selectedFilters.dateRange === 'Other Month' && selectedFilters.otherMonth) {
      params.append('om', selectedFilters.otherMonth);
    }
    if (selectedFilters.dateRange === 'Other Quarter' && selectedFilters.otherQuarter) {
      params.append('oq', selectedFilters.otherQuarter);
    }
    if (selectedFilters.dateRange === 'Other Year' && selectedFilters.otherYear) {
      params.append('oy', selectedFilters.otherYear);
    }

    if (selectedFilters.executive && selectedFilters.executive !== 'all') {
      params.append('eID', selectedFilters.executive);
    }

    navigate(`/threads-report?${params.toString()}`);
  };
  const renderEmptyState = () => {
    // Determine Thread Type Name for dynamic titles
    const getTypeName = (id) => threadTypes.find(t => t.TID === id)?.TypSng || "Thread";

    // Map current state to the configuration types
    const currentTID = parseInt(selectedFilters.threadType);
    const currentRType = reasonType === "DISQRSN" ? 2 : 1;

    // Configuration array mapping
    const configs = [
      { tID: 1, rType: 1, icon: TrendingDown, desc: isInqOnlyMode ? "Analyze the root causes and trends behind inquiry failures." : `Analyze the root causes and trends behind ${getTypeName(1)} failures.` },
      { tID: 2, rType: 1, icon: TrendingDown, desc: `Analyze ${getTypeName(2)} failure reasons.` },
      { tID: 3, rType: 1, icon: TrendingDown, desc: `Analyze ${getTypeName(3)} failure reasons.` },
      { tID: 1, rType: 2, icon: UserX, desc: isInqOnlyMode ? "Analyze the reasons for disqualification and identify underlying trends." : `Analyze the reasons for ${getTypeName(1)} disqualification.` },
      { tID: 2, rType: 2, icon: UserX, desc: `Analyze disqualified ${getTypeName(2)}.` },
      { tID: 3, rType: 2, icon: UserX, desc: `Analyze disqualified ${getTypeName(3)}.` },
    ];

    // Find matching config or fallback to a default
    const activeConfig = configs.find(c => c.tID === currentTID && c.rType === currentRType) || {
      icon: reasonType === "DISQRSN" ? UserX : TrendingDown,
      desc: reasonType === "DISQRSN" ? "Analyze disqualification reasons." : "Analyze failure reasons."
    };

    const IconComponent = activeConfig.icon;

    return (
      <div className="w-full">
        <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white shadow-sm w-full md:w-fit md:min-w-[500px]">
          <div className="flex-shrink-0 p-2 bg-gray-50 rounded-md">
            <IconComponent className="h-6 w-6 text-gray-400" strokeWidth={2} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-gray-700 leading-tight">
              {activeConfig.desc}
            </span>
            <span className="text-xs font-medium text-gray-400">
              No data available right now.
            </span>
          </div>
        </div>
      </div>
    );
  };
  const getDynamicChartHeight = (data) => {
    const baseChartHeight = isMobile ? 220 : 200; // The actual space for the Pie
    const legendRowHeight = isMobile ? 25 : 20; // Estimated height per legend row
    const itemsPerRow = isMobile ? 2 : 3; // Approximate items that fit in one line
    const numRows = Math.ceil(data?.length / itemsPerRow);

    return baseChartHeight + (numRows * legendRowHeight);
  };

  const dynamicHeight = useMemo(() => {
    // Safety check: if no data, return a default height
    if (!processedTableData?.data || processedTableData.data.length === 0) {
      return 300;
    }
    return getDynamicChartHeight(processedTableData.data);
  }, [processedTableData.data, isMobile]);
  // Fix #6: Conditional Logic for showing graphs
  const showCharts = processedTableData.data.length > 0;

  const hasActiveFiltersOrSearch = useMemo(() => {
    return (
      selectedFilters.assignedTo !== "all" ||
      selectedFilters.dateRange !== "This Month" ||
      selectedFilters.threadType !== 1 ||
      searchTerm !== "" ||
      tempFilters.assignedTo !== "all" ||
      tempFilters.dateRange !== "This Month"
    );
  }, [selectedFilters, tempFilters, searchTerm]);
  const currencySymbol = useCurrency();
  const emptyStateDescription =
    reasonType === "DISQRSN"
      ? "Analyze the reasons for disqualification and identify underlying trends."
      : "Analyze the reasons for failure and identify underlying trends.";

  const ReportTable = () => {


    // 1. Calculate Totals
    const totals = useMemo(() => {
      return currentTableData.reduce(
        (acc, item) => ({
          threadCount: acc.threadCount + (Number(item.threadCount) || 0),
          countShare: acc.countShare + (parseFloat(item.countShare) || 0),
          totalValue: acc.totalValue + (Number(item.totalValue) || 0),
          valueShare: acc.valueShare + (parseFloat(item.valueShare) || 0),
        }),
        { threadCount: 0, countShare: 0, totalValue: 0, valueShare: 0 }
      );
    }, [currentTableData]);

    return (
      <div className="space-y-4">
        <div className={cn("overflow-x-auto", isMobile && "overflow-x-hidden")}>
          {isMobile ? (
            /* Mobile Grid View */
            <div className="w-full">
              <div className="grid grid-cols-[45%_25%_30%] bg-gray-50 py-1.5 h-11 border-b">
                <div className="font-semibold text-sm text-black pl-4 flex items-center">
                  Reason
                </div>
                {valueType === "count" ? (
                  <>
                    <div className="font-semibold text-sm text-black flex items-center justify-end pr-2">
                      Count
                    </div>
                    <div className="font-semibold text-sm text-black flex items-center justify-end pr-3">
                      Share
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-sm text-black flex items-center justify-end pr-3">
                      Value
                    </div>
                    <div className="font-semibold text-sm text-black flex items-center justify-end pr-3">
                      Share
                    </div>
                  </>
                )}
              </div>

              <div className="divide-y divide-gray-200">
                {currentTableData.map((item, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-[45%_25%_30%] hover:bg-gray-50 h-10 items-center cursor-pointer"
                    onClick={() => handleRowClick(item.failureReason)}
                  >
                    <div className="pl-4 truncate text-sm text-text-color">
                      {item.failureReason}
                    </div>
                    {valueType === "count" ? (
                      <>
                        {/* Fixed Mobile Count Alignment */}
                        <div className="text-right pr-2 text-sm text-text-color">
                          {item.threadCount}
                        </div>
                        <div className="text-right pr-3 text-sm text-text-color">
                          {parseFloat(item.countShare).toFixed(2)}%
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-right pr-3 text-sm text-text-color">
                          <FormatAmount
                            amount={item.totalValue}
                            currency={currencySymbol}
                            showCurr={false}
                          />
                        </div>
                        <div className="text-right pr-3 text-sm text-text-color">
                          {parseFloat(item.valueShare).toFixed(2)}%
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Mobile Total Row */}
                <div className="grid grid-cols-[45%_25%_30%] bg-gray-50 h-11 items-center border-t border-gray-300">
                  <div className="pl-4 text-sm text-black font-semibold">
                    Total
                  </div>
                  {valueType === "count" ? (
                    <>
                      <div className="text-right pr-2 text-sm text-black font-semibold">
                        {totals.threadCount}
                      </div>
                      <div className="text-right pr-3 text-sm text-black font-semibold">
                        {totals.countShare.toFixed(0)}%
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-right pr-3 text-sm text-black font-semibold">
                        {formatAmount(totals.totalValue)}
                      </div>
                      <div className="text-right pr-3 text-sm text-black font-semibold">
                        {totals.valueShare.toFixed(0)}%
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Table className="min-w-full table-auto">
              <TableHeader>
                {/* CHANGE bg-bg-white TO bg-gray-50/50 */}
                <TableRow className="bg-gray-50/50 py-1.5 h-11 border-b">
                  <TableHead
                    className="text-left pl-4 font-semibold text-sm text-black cursor-pointer"
                    onClick={() => handleSort("failureReason")}
                  >
                    <div className="flex items-center">
                      {reasonType === "DISQRSN"
                        ? "Disqualification Reason"
                        : "Failure Reason"}
                      {sortConfig.key === "failureReason" &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>

                  {/* Count Header - Right Aligned */}
                  <TableHead
                    className="text-right pr-4 font-semibold text-sm text-black cursor-pointer w-[12%]"
                    onClick={() => handleSort("threadCount")}
                  >
                    <div className="flex items-center justify-end">
                      Count
                      {sortConfig.key === "threadCount" &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>

                  {/* Count Share - Right Aligned */}
                  <TableHead
                    className="text-right pr-4 font-semibold text-sm text-black cursor-pointer w-[12%]"
                    onClick={() => handleSort("countShare")}
                  >
                    <div className="flex items-center justify-end">
                      Share
                      {sortConfig.key === "countShare" &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>

                  {/* Value Header - Right Aligned */}
                  <TableHead
                    className="text-right pr-4 font-semibold text-sm text-black cursor-pointer w-[18%]"
                    onClick={() => handleSort("totalValue")}
                  >
                    <div className="flex items-center justify-end">
                      Value ({currencySymbol})
                      {sortConfig.key === "totalValue" &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>

                  {/* Value Share - Right Aligned */}
                  <TableHead
                    className="text-right pr-6 font-semibold text-sm text-black cursor-pointer w-[12%]"
                    onClick={() => handleSort("valueShare")}
                  >
                    <div className="flex items-center justify-end">
                      Share
                      {sortConfig.key === "valueShare" &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ArrowDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {currentTableData.map((item, idx) => (
                  <TableRow
                    key={idx}
                    className="hover:bg-gray-50 border-b h-10 cursor-pointer"
                    onClick={() => handleRowClick(item.failureReason)}
                  >
                    <TableCell className="text-left pl-4 max-w-[250px] truncate text-sm text-text-color">
                      {item.failureReason}
                    </TableCell>
                    {/* Numerical Data - Right Aligned */}
                    <TableCell className="text-right pr-4 text-sm text-text-color">
                      {item.threadCount}
                    </TableCell>
                    <TableCell className="text-right pr-4 text-sm text-text-color">
                      {parseFloat(item.countShare).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right pr-4 text-sm text-text-color">
                      <FormatAmount
                        amount={item.totalValue}
                        currency={currencySymbol}
                        showCurr={false}
                      />
                    </TableCell>
                    <TableCell className="text-right pr-6 text-sm text-text-color">
                      {parseFloat(item.valueShare).toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              {/* Footer - Styled to match Header exactly */}
              <tfoot className="bg-gray-50/50 border-t border-gray-200">
                <TableRow className="h-11 hover:bg-transparent">
                  <TableCell className="text-left pl-4 font-semibold text-sm text-black align-middle">
                    Total
                  </TableCell>
                  <TableCell className="text-right pr-4 font-semibold text-sm text-black align-middle">
                    {totals.threadCount}
                  </TableCell>
                  <TableCell className="text-right pr-4 font-semibold text-sm text-black align-middle">
                    100.00%
                  </TableCell>
                  <TableCell className="text-right pr-4 font-semibold text-sm text-black align-middle">
                    <FormatAmount
                      amount={totals.totalValue}
                      currency={currencySymbol}
                      showCurr={false}
                    />
                  </TableCell>
                  <TableCell className="text-right pr-6 font-semibold text-sm text-black">
                    100.00%
                  </TableCell>
                </TableRow>
              </tfoot>
            </Table>
          )}
        </div>
      </div>
    );
  };
  return (
    <TooltipProvider>
      <style>{legendStyle}</style>
      <div className={`lg:space-y-6 overflow-x-hidden pb-4`}>
        {/* ----------------- Header Section ----------------- */}
        <div className="hidden lg:flex justify-between items-center gap-3 h-12">
          <div className="flex items-center flex-grow">
            <h1 className="text-3xl font-bold text-text-color mr-8 web-header-name">
              {reasonType === "DISQRSN"
                ? "Disqualification Analysis"
                : "Failure Analysis"}
            </h1>

            {/* Search and Summary Badges */}
            <div className="flex items-center justify-end flex-grow gap-3">
              {/*
                  <div className={cn("flex items-center px-3 lg:px-4 h-8 lg:h-10", "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm")}>
                    {/* <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                      <span>Count:</span>
                      <span className="font-medium">{processedTableData.totalCount}</span>
                    </span> */}
              {/* </div>
                  <div className={cn("flex items-center px-3 lg:px-4 h-8 lg:h-10", "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm")}>
                    <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                      <span>Value:</span>
                      <span className="font-medium">{currencySymbol}{processedTableData.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                    </span>
                  </div>
                  */}
              {rawData.length > 10 && (
                <div className="relative w-120 filter-border">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                  <Input
                    placeholder="Search Reason"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                  />
                </div>
              )}
            </div>

          </div>

          {/* Filter Button */}
          <div className="flex items-center gap-3 h-full">
            <Button
              className={cn(
                "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] relative",
                isFilterSelected && "filter-active"
              )}
              onClick={() => setIsFilterSelected((prev) => !prev)}
            >
              <div className="h-6 w-6 flex items-center justify-center transition-all">
                <Filter
                  className={cn(
                    "h-4 w-4",
                    isFilterSelected
                      ? "text-sahi-primary-color"
                      : "text-white group-hover:text-sahi-primary-color"
                  )}
                />
              </div>
            </Button>
          </div>
        </div>

        {/* ----------------- Filter Bar (Desktop) ----------------- */}
        {isFilterSelected && !isMobile && (
          <div className="hidden lg:flex flex-wrap gap-4 items-center mb-2">
            {/* Period Filter Component */}
            <PeriodFilter
              value={selectedFilters}
              onChange={(newFilters) => {
                setSelectedFilters(newFilters);
                setTempFilters(newFilters);
              }}
              monthlyData={monthlyJson}
              showMonthFilter={true}
              showQuarterFilter={true}
              showYearFilter={true}
            />

            {/* Thread Type Filter (if not in Inquiry Only Mode) */}
            {!isInqOnlyMode && threadTypeFilter.map((filter) => (
              <CommonFilter
                key={filter.key}
                label={filter.displayValue(selectedFilters[filter.key])}
                options={filter.options}
                value={selectedFilters[filter.key]}
                onChange={(value) => {
                  setSelectedFilters((prev) => ({ ...prev, [filter.key]: value }));
                  setTempFilters((prev) => ({ ...prev, [filter.key]: value }));
                }}
                displayValue={filter.displayValue}
                type={filter.type}
                mobileLabel={filter.MobileLabel}
                showAllOption={filter.showAllOption}
              />
            ))}

            {!isSmartSellerMode() && !isAssignedOnly ? <>
              {/* Executive Filter */}
              <CommonFilter
                key="assignedTo"
                label={
                  selectedFilters.assignedTo === "unassigned"
                    ? "Not Assigned"
                    : selectedFilters.assignedTo === "all"
                      ? "All Executives"
                      : users.find(
                        (u) => String(u.userID) === String(selectedFilters.assignedTo)
                      )?.name || "All Executives"
                }
                options={[
                  { name: "Not Assigned", ID: "unassigned" },
                  ...users
                    .map((u) => ({ name: u.name, ID: u.userID }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
                ]}
                value={selectedFilters.assignedTo}
                onChange={(value) => {
                  setSelectedFilters((prev) => ({ ...prev, assignedTo: value }));
                  setTempFilters((prev) => ({ ...prev, assignedTo: value }));
                }}
                displayValue={(val) => {
                  if (val === "unassigned") return "Not Assigned";
                  if (val === "all") return "All Executives";
                  const foundUser = users.find((u) => String(u.userID) === String(val));
                  return foundUser?.name || "All Executives";
                }}
                type="list"
                mobileLabel="Executive"
                showAllOption={true}
                searchEnabled={true}
              /> </> : null
            }

            {/* Category Filter (if categories exist) */}
            {distinctCategories.length > 0 && (
              <CommonFilter
                key="category"
                label={selectedFilters.category === "all" ? "All Categories" : selectedFilters.category}
                options={distinctCategories.map((cat) => ({ name: cat, ID: cat }))}
                value={selectedFilters.category}
                onChange={(value) => {
                  setSelectedFilters((prev) => ({ ...prev, category: value }));
                  setTempFilters((prev) => ({ ...prev, category: value }));
                }}
                displayValue={(val) => val === "all" ? "All Categories" : val}
                type="list"
                mobileLabel="Category"
                showAllOption={true}
                searchEnabled={true}
              />
            )}
          </div>
        )}

        {/* ----------------- Filter Drawer (Mobile) ----------------- */}
        {isFilterSelected && isMobile && (
          <Drawer
            open={isDrawerOpen}
            onOpenChange={() => {
              setIsDrawerOpen(false);
              setIsFilterSelected(false);
            }}
          >
            <DrawerContent className="p-0 [&>div:first-child]:hidden">
              <div className="f-col h-[100dvh] !rounded-t-[10px]">
                {/* Header */}
                <div className="flex plain items-center justify-between p-4 border-b-2 border-gray-200 text-xl !rounded-t-[10px]">
                  <p className="text-lg font-semibold text-sahi-primary-color">
                    Filters & Sorting
                  </p>
                  <button
                    className="group flex items-center gap-1 text-sm font-light hover:underline transition duration-200 text-sahi-primary-color"
                    onClick={() => {
                      handleResetFilters();
                      setIsFilterSelected(false);
                    }}
                  >
                    <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                    <span>Reset</span>
                  </button>
                </div>

                {/* Body */}
                <div className="flex flex-1 overflow-y-auto">
                  <div className="flex-1 p-3 overflow-y-auto filter-scroll-container">
                    {filterOptions.map(
                      ({
                        key,
                        label,
                        options,
                        displayValue,
                        disabled,
                        mobiledisabled,
                        mobileDropdownText,
                        MobileLabel,
                        showAllOption,
                        type,
                      }) => {
                        // Only show sub-filters when their parent dateRange is selected
                        const shouldShow =
                          key === "dateRange" ||
                          // key === "threadType" ||
                          key === "category" ||
                          key === "assignedTo" ||
                          (key === "otherMonth" && tempFilters.dateRange === "Other Month") ||
                          (key === "otherQuarter" && tempFilters.dateRange === "Other Quarter") ||
                          (key === "otherYear" && tempFilters.dateRange === "Other Year");

                        if (!shouldShow) return null;

                        return (
                          <div key={key} id={key} className="mb-4">
                            <DrawerFilter
                              id={key}
                              label={label}
                              options={options}
                              value={tempFilters[key] || ""}
                              displayValue={displayValue}
                              onChange={(value) =>
                                setTempFilters((prev) => ({
                                  ...prev,
                                  [key]: value,
                                }))
                              }
                              disabled={mobiledisabled || disabled}
                              type={type || "list"}
                              mobileDropdownText={mobileDropdownText}
                              MobileLabel={MobileLabel}
                              showAllOption={showAllOption}
                            />
                          </div>
                        );
                      })}




                  </div>
                </div>
                {/* Footer */}
                <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-sahi-disable">
                  <DrawerClose>
                    <div className="f-row justify-start gap-2">
                      <Button
                        className="sahi-primary-button"
                        onClick={handleApplyFilters}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        className="sahi-secondary-button"
                        onClick={handleCancelFilters}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {rawData.length === 0 && !hasActiveFilters && !isLoading && (
          <div className={cn("flex flex-col items-start  w-full ", isMobile ? "px-3 pt-3 " : "px-0")}>
            {renderEmptyState()}
          </div>
        )}

        {((rawData.length > 0 && processedTableData.data.length === 0) || (rawData.length === 0 && hasActiveFilters)) && !isLoading && (
          <div className={cn("flex flex-col items-start pt-3 w-full lg:pt-0", isMobile ? "px-3" : "px-0")}>
            <div className="f-col items-start gap-5 mt-0 lg:pt-0 lg:px-0 w-full">
              <div className="no-records border border-gray-200 shadow-sm sahi-radius p-4 bg-white flex items-center w-full md:w-fit">
                <span className="text-gray-600 text-sm font-medium">
                  No records found for the selected filters.
                </span>
                <button
                  onClick={handleResetFilters}
                  className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2"
                >
                  Clear filter.
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Present */}
        {processedTableData.data.length > 0 && (
          <div
            className={cn(
              "grid grid-cols-1 gap-3 items-start rounded-lg",
              // Fix #6: Only show 2 cols if graphs are visible
              showCharts ? "lg:grid-cols-2 lg:gap-6" : ""
            )}
          >
            {/* ---------------- Mobile View ---------------- */}
            {isMobile && (
              <>
                <div className="flex w-full border-b border-gray-200 bg-white">
                  <button
                    onClick={() => setValueType("count")}
                    className={cn(
                      "flex-1 py-3 text-sm font-medium transition-all relative",
                      valueType === 'count'
                        ? "text-sahi-primary-color border-b-2 border-sahi-green"
                        : "text-gray-500"
                    )}
                  >
                    Count
                  </button>
                  <button
                    onClick={() => setValueType("value")}
                    className={cn(
                      "flex-1 py-3 text-sm font-medium transition-all relative",
                      valueType === "value"
                        ? "text-sahi-primary-color border-b-2 border-sahi-green"
                        : "text-gray-500"
                    )}
                  >
                    Value
                  </button>
                </div>

                {/* Fix #6: Hide Graphs if only 1 item */}
                {/* Mobile Chart Section */}
                {showCharts && (
                  <Card className="bg-white border border-gray-200 shadow-sm rounded-lg mb-4">
                    <CardContent className="p-2">
                      <div style={{ width: '100%', height: `${dynamicHeight}px` }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={processedTableData.data.map((item) => ({
                                name: item.failureReason,
                                value: valueType === "count" ? item.threadCount : item.totalValue,
                                percentage: parseFloat(valueType === "count" ? item.countShare : item.valueShare),
                              }))}
                              cx="50%"
                              cy="110" // Fixed Y position to keep Pie at the top
                              outerRadius={80}
                              innerRadius={55}
                              dataKey="value"
                              paddingAngle={2}
                              isAnimationActive={false}
                              style={{ outline: "none" }}
                            >
                              {processedTableData.data.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={themeColors[index % themeColors.length]} stroke="none" />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                              formatter={(value, name, props) => [
                                `${valueType === "value" ? currencySymbol : ""}${value.toLocaleString("en-IN")} (${props.payload.percentage.toFixed(2)}%)`,
                                name,
                              ]}
                            />
                            <Legend
                              verticalAlign="bottom"
                              align="center"
                              content={({ payload }) => (
                                <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 mt-4 px-2 w-full">
                                  {payload.map((entry, index) => (
                                    <div key={`legend-${index}`} className="flex items-center gap-1.5">
                                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                                      <span className="text-[11px] font-medium text-gray-700">
                                        {entry.value}
                                        <span className="text-gray-400 font-normal ml-1">
										({Number(
											processedTableData?.data?.[index]?.[
											valueType === 'count' ? 'countShare' : 'valueShare'
											] || 0
										).toFixed(2)}
										%)
										</span>

                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-sahi-white !pt-0 overflow-hidden border border-gray-200 shadow-sm h-fit mt-0">
                  <CardContent className="px-0 py-0">
                    <ReportTable />
                  </CardContent>
                </Card>
              </>
            )}

            {/* ---------------- Desktop View ---------------- */}
            {!isMobile && (
              <>
                <div className="flex flex-col mb-6">
                  <Card className="bg-sahi-white !pt-0 overflow-hidden border border-gray-200 shadow-sm h-fit">
                    <CardContent className="px-0 py-0 !sahi-radius">
                      <ReportTable />
                    </CardContent>
                  </Card>

                  {processedTableData.data.length > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(
                        processedTableData.data.length / rowsPerPage
                      )}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(size) => setRowsPerPage(size)}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  )}
                </div>

                {/* Desktop View Graphs */}
                {showCharts && (
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { title: "Count Distribution", dataKey: "threadCount", shareKey: "countShare", type: "count" },
                      { title: "Value Distribution", dataKey: "totalValue", shareKey: "valueShare", type: "value" }
                    ].map((chartConfig) => (
                      <Card key={chartConfig.type} className="bg-white border border-gray-200 shadow-sm rounded-lg flex flex-col h-full">
                        {/* 1. TITLE - Reduced padding to be closer to graph */}
                        <CardHeader className="pt-3 pb-0 px-3 bg-transparent text-center">
                          <CardTitle className="text-sm font-semibold text-gray-700">
                            {chartConfig.title}
                          </CardTitle>
                        </CardHeader>

                        <CardContent className="p-0 flex flex-col items-center">
                          {/* 2. GRAPH - Negative top margin to pull closer to title */}
                          <div className="w-full h-[220px] -mt-4">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                <Pie
                                  data={processedTableData.data.map((item) => ({
                                    name: item.failureReason,
                                    value: item[chartConfig.dataKey],
                                    percentage: parseFloat(item[chartConfig.shareKey]),
                                  }))}
                                  cx="50%"
                                  cy="47%" // Centers the pie vertically within the 220px box
                                  outerRadius={75}
                                  innerRadius={50}
                                  paddingAngle={2}
                                  dataKey="value"
                                  isAnimationActive={false}
                                >
                                  {processedTableData.data.map((_, idx) => (
                                    <Cell
                                      key={`cell-${idx}`}
                                      fill={themeColors[idx % themeColors.length]}
                                      stroke="none"
                                    />
                                  ))}
                                </Pie>
                                <Tooltip
                                  contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                  formatter={(value, name, props) => [
                                    `${chartConfig.type === 'value' ? (currencySymbol || '₹') : ''}${value.toLocaleString("en-IN")} (${props.payload.percentage.toFixed(2)}%)`,
                                    name,
                                  ]}
                                />
                                {/* Internal Legend is REMOVED to save space */}
                              </PieChart>
                            </ResponsiveContainer>
                          </div>

                          {/* 3. LEGEND - Placed directly under graph with dynamic height */}
                          <div className="px-4 pb-4 -mt-2">
                            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5">
                              {processedTableData.data.map((item, idx) => (
                                <div key={`legend-${idx}`} className="flex items-center gap-1.5">
                                  {/* Circle Icon */}
                                  <div
                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: themeColors[idx % themeColors.length] }}
                                  />
                                  <span className="text-[11px] font-medium text-gray-600 leading-none">
                                    {item.failureReason}
                                    <span className="text-gray-400 ml-1 font-normal">
                                      ({parseFloat(chartConfig.type === 'count' ? item.countShare : item.valueShare).toFixed(2)}%)
                                    </span>
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ThrLossAnalysis;
